/**
 * Capitalizes the first letter and lowercase the rest (useful for i18n)
 * @param word the word to capitalize the first letter
 * @returns {string} a new string with capitalized first letter and lowercase rest
 */
export const capitalizeFirst = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/**
 * Checks if any string in the array exactly matches the target string
 */
export const hasExactMatch = (candidates: string[], target: string): boolean => candidates.some(candidate => candidate === target);

/**
 * Checks if there's bidirectional inclusion between any candidate and the target
 * (candidate includes target OR target includes candidate)
 */
export const hasBidirectionalInclusion = (candidates: string[], target: string): boolean =>
  candidates.some(candidate => candidate.includes(target) || target.includes(candidate));

/**
 * Calculates a match quality score for string matching
 * Higher scores indicate better matches
 *
 * @param candidates - Array of candidate strings to check against
 * @param target - The target string to match
 * @returns Positive score if target contains any candidate, negative otherwise
 */
export const calculateMatchScore = (candidates: string[], target: string): number => {
  const containsCandidate = candidates.some(candidate => target.includes(candidate));
  return containsCandidate ? target.length : -target.length;
};

/**
 * Finds the best matching string from an array based on exact and partial matching
 *
 * @param candidates - Array of candidate strings to search through
 * @param targets - Array of target strings to match against
 * @returns Best matching candidate or null if no match found
 */
export const findBestMatch = (candidates: string[], targets: string[]): string | null => {
  if (candidates.length === 0 || targets.length === 0) return null;

  const validTargets = targets.filter(Boolean);
  if (validTargets.length === 0) return null;

  // First: try exact matches
  const exactMatch = candidates.find(candidate => hasExactMatch(validTargets, candidate));
  if (exactMatch) return exactMatch;

  // Second: find best partial match
  const partialMatches = candidates.filter(candidate => hasBidirectionalInclusion(validTargets, candidate));

  if (partialMatches.length === 0) return null;

  // Sort by match quality score (highest first)
  return partialMatches.sort((a, b) => calculateMatchScore(validTargets, b) - calculateMatchScore(validTargets, a))[0];
};

/**
 * Creates a matcher function for a specific set of target strings
 * Useful for creating reusable matchers
 */
export const createMatcher = (targets: string[]) => ({
  findExactMatch: (candidates: string[]) => candidates.find(candidate => hasExactMatch(targets, candidate)) || null,

  findBestMatch: (candidates: string[]) => findBestMatch(candidates, targets),

  hasMatch: (candidates: string[]) => findBestMatch(candidates, targets) !== null,
});
